/**
 * MapResolvingLoader Component
 * Fills the map canvas slot while geocoding is in flight, as the baked Globe
 * orbit treatment: one CSS meridian sphere — the same object the compact tile's
 * pocket orb draws, scaled up — with markers circling it and being drawn inward
 * one at a time, and the location names ledgered beside it, each row blooming
 * as its marker is taken. It answers the tweak "a spinning globe that'd feel
 * like it's searching where within it the points are".
 *
 * The names ARE the loader (the standalone MapLocationList below the stage is
 * suppressed by MapSurface while this renders), and everything is CSS-only so
 * a second MapLibre context is never mounted.
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  mapLocationRowText,
  type MapLocationEntry,
} from './map-render-model';
import './MapResolvingLoader.css';

const STAGE_RADIUS = 10;
/** Names woven into the loading stage before the overflow chip takes over. */
const RESOLVING_NAME_LIMIT = 5;

/** Place names arrive in any script, and `markdown-hebrew.ts` covers markdown
 *  text elements only — it never reaches this DOM, so each name resolves its
 *  own direction. */
const AUTO_TEXT_DIRECTION = 'auto';

const STAGE_STYLE: CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: STAGE_RADIUS,
  border: '1px solid rgba(102, 254, 193, 0.14)',
  backgroundColor: 'rgba(255,255,255,0.03)',
  backgroundImage: 'radial-gradient(120% 90% at 50% 0%, rgba(102, 254, 193, 0.08), transparent 55%)',
};

const STAGE_TEXT_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
};

/** The root the CSS keys every organ — and the cycle length they all share —
 *  off. One treatment now, so it carries no modifier. */
const ROOT_CLASS_NAME = 'map-resolving-loader';

/** Seconds between one ledger row's turn and the next, so the search reaches
 *  the rows in list order: the orbit cycle split across the row cap — never
 *  across however many places actually come back, which nothing here is
 *  allowed to know. */
const LEDGER_PHASE_STEP = 1.5;

/** The ledger stagger is the entrance only — the search phase runs on a much
 *  longer beat, so the two delays are kept apart. */
const ROW_ENTRANCE_STEP = 0.09;

/** Fixed seats on the sphere for the search to find. Three of them always,
 *  whatever the block named: a loading state cannot preview its own result. */
enum MapResolvingPipSeat {
  North = 'north',
  Mid = 'mid',
  South = 'south',
}

const PIP_SEATS: MapResolvingPipSeat[] = [
  MapResolvingPipSeat.North,
  MapResolvingPipSeat.Mid,
  MapResolvingPipSeat.South,
];

/** Orbit seats — four markers on quarter phases, again independent of the
 *  eventual count. Each seat class carries both its phase offset and its
 *  parked angle for reduced motion. */
enum MapResolvingOrbitSeat {
  First = 'first',
  Second = 'second',
  Third = 'third',
  Fourth = 'fourth',
}

const ORBIT_SEATS: MapResolvingOrbitSeat[] = [
  MapResolvingOrbitSeat.First,
  MapResolvingOrbitSeat.Second,
  MapResolvingOrbitSeat.Third,
  MapResolvingOrbitSeat.Fourth,
];

export interface MapResolvingLoaderProps {
  dataElScope: DataElScope;
  /** Everything the block named — the loader renders these as the ledger. */
  entries: MapLocationEntry[];
  accessibleName: string;
}

interface ResolvingPlace {
  key: string;
  name: string;
}

function resolvingPlaces(entries: MapLocationEntry[]): ResolvingPlace[] {
  return entries.slice(0, RESOLVING_NAME_LIMIT).map((entry) => ({
    key: entry.key,
    name: mapLocationRowText(entry).primary,
  }));
}

export function MapResolvingLoader({
  dataElScope,
  entries,
  accessibleName,
}: MapResolvingLoaderProps): ReactElement {
  const places = resolvingPlaces(entries);
  const hiddenCount = Math.max(0, entries.length - places.length);

  const searchInstrument = (
    <span className="map-resolving-loader__globe-shell" aria-hidden>
      <span className="map-resolving-loader__globe">
        <span className="map-resolving-loader__globe-wire">
          <span className="map-resolving-loader__globe-spin">
            <span className="map-resolving-loader__globe-meridian" />
            <span className="map-resolving-loader__globe-meridian map-resolving-loader__globe-meridian--mid" />
            <span className="map-resolving-loader__globe-meridian map-resolving-loader__globe-meridian--late" />
          </span>
          {/* Parallels sit outside the spin group: a horizontal circle does not
              move under a turn about the polar axis. */}
          <span className="map-resolving-loader__globe-parallel map-resolving-loader__globe-parallel--high" />
          <span className="map-resolving-loader__globe-parallel map-resolving-loader__globe-parallel--equator" />
          <span className="map-resolving-loader__globe-parallel map-resolving-loader__globe-parallel--low" />
        </span>
        {PIP_SEATS.map((seat) => (
          <span
            key={seat}
            className={`map-resolving-loader__globe-pip map-resolving-loader__globe-pip--${seat}`}
          />
        ))}
      </span>
      <span className="map-resolving-loader__orbit">
        {ORBIT_SEATS.map((seat) => (
          <span
            key={seat}
            className={`map-resolving-loader__orbit-arm map-resolving-loader__orbit-arm--${seat}`}
          >
            <span className="map-resolving-loader__orbit-rail">
              <span className="map-resolving-loader__orbit-marker" />
            </span>
          </span>
        ))}
      </span>
    </span>
  );

  return (
      <div
        data-el={dataEl(dataElScope, 'MapResolvingLoader/Root')}
        className={ROOT_CLASS_NAME}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
        aria-label={accessibleName}
        aria-busy
      >
        <div data-el={dataEl(dataElScope, 'MapResolvingLoader/Surface')} style={STAGE_STYLE}>
          <div
            className="map-resolving-loader__fill map-resolving-loader__split"
          >
            {searchInstrument}
            <div className="map-resolving-loader__ledger" style={STAGE_TEXT_STYLE}>
              {places.map((place, index) => {
                const phaseDelay = `${(index * LEDGER_PHASE_STEP).toFixed(2)}s`;
                const entranceDelay = `${(index * ROW_ENTRANCE_STEP).toFixed(2)}s`;
                return (
                  <div
                    key={place.key}
                    className="map-resolving-loader__ledger-row"
                    style={{ animationDelay: entranceDelay }}
                  >
                    <span
                      className="map-resolving-loader__row-glow"
                      style={{ animationDelay: phaseDelay }}
                      aria-hidden
                    />
                    <span
                      className="map-resolving-loader__pending-dot"
                      style={{ animationDelay: phaseDelay }}
                      aria-hidden
                    />
                    <span
                      className="map-resolving-loader__row-name"
                      dir={AUTO_TEXT_DIRECTION}
                    >
                      {place.name}
                    </span>
                  </div>
                );
              })}
              {hiddenCount > 0 ? (
                <div
                  className="map-resolving-loader__overflow"
                  style={STAGE_TEXT_STYLE}
                >
                  {`+${hiddenCount} more`}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
  );
}
